import { useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import logo from "@/assets/habits365greek-logo.png";
import { PortalSidebar } from "@/components/navigation/PortalSidebar";
import { PortalFooter } from "@/components/PortalFooter";
import { usePermissions } from "@/contexts/PermissionsContext";
import { useClientAuth } from "../hooks/useClientAuth";
import { CLIENT_NAV } from "../nav";
import { OrderingGuidelinesBanner } from "./OrderingGuidelinesBanner";

/**
 * Top-level layout for every authenticated client page.
 *
 * Uses the SHARED `PortalSidebar` (same component the admin dashboard uses) so
 * the two portals look + behave identically; nav visibility is gated by the
 * `module.my-*` capabilities in the DB (role_capabilities). The slim top bar
 * (account menu) + footer + mobile drawer remain here.
 */

interface ClientShellProps {
  children: React.ReactNode;
}

const ClientBrand = () => (
  <Link to="/client/catalog" className="flex items-center gap-2">
    <img
      src={logo}
      alt="Habits 365 Greek"
      className="h-7 object-contain dark:invert dark:brightness-200"
    />
    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary">
      Client
    </span>
  </Link>
);

const ClientFooter = () => (
  <div className="text-xs text-muted-foreground">
    Need help?
    <br />
    <a href="mailto:hello@habits365greek.com" className="text-primary hover:underline">
      hello@habits365greek.com
    </a>
  </div>
);

export const ClientShell = ({ children }: ClientShellProps) => {
  const { currentUser, logout } = useClientAuth();
  const { userHasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const visibleNav = useMemo(
    () => CLIENT_NAV.filter((i) => !i.capability || userHasPermission(i.capability)),
    [userHasPermission],
  );

  const handleLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/client/login");
  };

  const initials =
    currentUser?.fullName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "?";

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* ── Shared desktop sidebar ── */}
      <div className="sticky top-0 hidden h-screen md:block">
        <PortalSidebar
          className="h-screen"
          ariaLabel="Client navigation"
          groups={[{ items: CLIENT_NAV }]}
          header={<ClientBrand />}
          footer={<ClientFooter />}
        />
      </div>

      {/* ── Main column ── */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-sticky border-b border-border bg-navbar/95 text-navbar-foreground backdrop-blur-sm">
          <div className="flex h-16 items-center gap-4 px-4 sm:px-6 lg:px-8">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle navigation"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            <Link to="/client/catalog" className="flex items-center gap-2 md:hidden">
              <img
                src={logo}
                alt="Habits 365 Greek"
                className="h-8 object-contain dark:invert dark:brightness-200"
              />
            </Link>

            <div className="ml-auto flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary transition-colors hover:bg-primary/15"
                    aria-label="Account menu"
                  >
                    {initials}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="space-y-0.5">
                    <div className="truncate text-sm font-semibold">
                      {currentUser?.fullName ?? "Guest"}
                    </div>
                    <div className="truncate text-xs font-normal text-muted-foreground">
                      {currentUser?.organizationName}
                      {currentUser?.chapterName ? ` — ${currentUser.chapterName}` : ""}
                    </div>
                    <div className="truncate text-xs font-normal text-muted-foreground">
                      {currentUser?.email}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate("/client/profile")}
                    className="cursor-pointer"
                  >
                    <User className="mr-2 h-4 w-4" />
                    View Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Mobile nav drawer — same capability-filtered items */}
          {mobileOpen && (
            <nav className="border-t border-border bg-background/95 p-3 backdrop-blur md:hidden">
              {visibleNav.map((item) => {
                const Icon = item.icon;
                const active =
                  location.pathname === item.path ||
                  location.pathname.startsWith(`${item.path}/`);
                return (
                  <NavLink
                    key={item.key}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors",
                      active
                        ? "bg-sidebar-active text-sidebar-active-foreground"
                        : "text-muted-foreground hover:bg-sidebar-hover hover:text-sidebar-foreground",
                    )}
                  >
                    <Icon className={cn("h-4 w-4 flex-shrink-0", item.iconClass)} />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          )}
        </header>

        {/* Ordering Guidelines — pinned to the shell's top container (above the
            scrollable page content), shown across the whole ordering journey:
            Catalog → My Events → My Quotes → My Orders. */}
        {["/client/catalog", "/client/designs", "/client/quotes", "/client/orders"].includes(
          location.pathname,
        ) && <OrderingGuidelinesBanner />}

        {/* Page content */}
        {/* Full-width content area + padding matched to the admin dashboard.
            `flex flex-col` so a page can opt into filling the viewport height
            (e.g. the Request-a-Design wizard) via a `flex-1` child. */}
        <main className="flex flex-1 flex-col overflow-auto px-4 py-6 sm:px-6 lg:px-8">
          {children}
        </main>

        {/* Global footer — defined bottom edge so long, auto-scrolling pages
            (e.g. My Events) don't feel cramped where the content ends. */}
        <PortalFooter />
      </div>
    </div>
  );
};

export default ClientShell;
